import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { requestPasswordReset } from '@/services/auth.service';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isSupabaseConfigured } from '@/services/supabase-client.service';
import { cn } from '@/lib/utils';
import { ShieldCheck, ArrowLeft, Mail, Leaf } from 'lucide-react';
import { LayoutGroup, motion } from 'framer-motion';
import { startFlow, flowStep, endFlow, mark, measure } from '@/lib/perf';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = (location.state as { returnUrl?: string })?.returnUrl || '/app';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [appSelection, setAppSelection] = useState<'praxis' | 'pflege'>('praxis');
  const [switchingToCare, setSwitchingToCare] = useState(false);

  const configured = isSupabaseConfigured();
  const careUrl = (import.meta.env.VITE_CARE_APP_URL as string) || 'https://pflege.getflowly.de';

  function switchToCareLogin() {
    if (switchingToCare) return;
    setAppSelection('pflege');
    setSwitchingToCare(true);
    window.setTimeout(() => {
      window.location.href = `${careUrl}/login`;
    }, 140);
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setResetError(null);

    if (!configured) {
      setResetError('Supabase ist nicht konfiguriert. Passwort-Reset ist aktuell nicht möglich.');
      return;
    }

    if (!resetEmail.trim()) {
      setResetError('Bitte E-Mail-Adresse eingeben.');
      return;
    }

    setResetLoading(true);
    try {
      const result = await requestPasswordReset(resetEmail.trim());
      if (result.success) {
        setResetSuccess(true);
      } else {
        setResetError(result.error || 'E-Mail konnte nicht gesendet werden.');
      }
    } catch {
      setResetError('Ein unerwarteter Fehler ist aufgetreten.');
    } finally {
      setResetLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const flow = startFlow('login');
    flowStep('login', 'click_submit', { returnUrl });
    mark('login_click');

    if (!configured) {
      endFlow('login', 'error', { reason: 'supabase_not_configured' });
      setError('Supabase ist nicht konfiguriert. Bitte VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY setzen.');
      return;
    }

    if (!email.trim() || !password.trim()) {
      endFlow('login', 'error', { reason: 'validation_missing_credentials' });
      setError('Bitte E-Mail und Passwort eingeben.');
      return;
    }

    setLoading(true);
    mark(`login_auth_request_start:${flow.id}`);
    flowStep('login', 'auth_request_start');

    try {
      const result = await login(email.trim(), password);
      mark(`login_auth_request_end:${flow.id}`);
      measure('login_auth_request_ms', `login_auth_request_start:${flow.id}`, `login_auth_request_end:${flow.id}`);
      flowStep('login', 'auth_request_end', { success: result.success });

      if (!result.success) {
        endFlow('login', 'error', { reason: result.error || 'auth_failed' });
        setError(result.error || 'Anmeldung fehlgeschlagen.');
        return;
      }

      flowStep('login', 'navigate_to_app');
      navigate(returnUrl, { replace: true });
    } catch {
      endFlow('login', 'error', { reason: 'unexpected_exception' });
      setError('Ein unerwarteter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-800 px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo + Heading */}
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 shadow-soft">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-neutral-900 dark:text-neutral-100">flowly Praxis</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-500">Melden Sie sich an, um fortzufahren</p>
        </div>

        <LayoutGroup id="login-app-toggle-practice">
          <div
            role="tablist"
            aria-label="App-Bereich wählen"
            className={cn(
              'relative grid grid-cols-2 rounded-xl border p-1 transition-colors duration-300',
              'border-neutral-200 dark:border-neutral-700',
              appSelection === 'pflege'
                ? 'bg-brand-50/60 dark:bg-neutral-800'
                : 'bg-neutral-100 dark:bg-neutral-800',
            )}
          >
            <button
              type="button"
              role="tab"
              aria-selected={appSelection === 'praxis'}
              onClick={() => setAppSelection('praxis')}
              className={cn(
                'relative flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-300',
                appSelection === 'praxis'
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-neutral-500 dark:text-neutral-400',
              )}
            >
              {appSelection === 'praxis' && (
                <motion.span
                  layoutId="login-app-pill"
                  transition={{ type: 'spring', stiffness: 460, damping: 36, mass: 0.62 }}
                  className="pointer-events-none absolute inset-0 rounded-lg bg-white dark:bg-neutral-700 shadow-soft ring-1 ring-brand-200/70 dark:ring-brand-600/30"
                  aria-hidden="true"
                />
              )}
              <ShieldCheck className="relative z-10 h-4 w-4" />
              <span className="relative z-10">Praxis</span>
            </button>

            <motion.button
              type="button"
              role="tab"
              aria-selected={appSelection === 'pflege'}
              onClick={switchToCareLogin}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.985 }}
              disabled={switchingToCare}
              className={cn(
                'relative flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-300',
                appSelection === 'pflege'
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-neutral-500 hover:text-brand-600 dark:text-neutral-400 dark:hover:text-brand-300',
                switchingToCare && 'cursor-wait opacity-80',
              )}
            >
              {appSelection === 'pflege' && (
                <motion.span
                  layoutId="login-app-pill"
                  transition={{ type: 'spring', stiffness: 460, damping: 36, mass: 0.62 }}
                  className="pointer-events-none absolute inset-0 rounded-lg bg-white dark:bg-neutral-700 shadow-soft ring-1 ring-brand-200/70 dark:ring-brand-600/30"
                  aria-hidden="true"
                />
              )}
              <Leaf className="relative z-10 h-4 w-4" />
              <span className="relative z-10">Pflege</span>
            </motion.button>
          </div>
        </LayoutGroup>

        {/* Hint: not connected */}
        {!configured && (
          <div className="rounded-lg border border-warning bg-warning-light/30 p-3 text-sm text-warning-dark">
            Supabase nicht konfiguriert. Bitte <code className="font-mono text-xs">.env.local</code>{' '}
            anlegen.
          </div>
        )}

        {/* Form: Login oder Reset */}
        {!resetMode ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="E-Mail"
              type="email"
              placeholder="arzt@praxis.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            <Input
              label="Passwort"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              showPasswordToggle
            />

            {error && (
              <div role="alert" aria-live="assertive" className="rounded-lg border border-error/30 bg-error-light/20 px-3 py-2 text-sm text-error-dark">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" loading={loading} disabled={loading || !configured}>
              Anmelden
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setResetMode(true); setResetEmail(email); setResetSuccess(false); setResetError(null); }}
                className="text-sm text-brand-600 hover:text-brand-700 hover:underline transition-colors"
              >
                Passwort vergessen?
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            {resetSuccess ? (
              /* Erfolgs-Nachricht */
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                  <p className="font-medium text-green-800">E-Mail gesendet!</p>
                  <p className="mt-1 text-sm text-green-700">
                    Eine E-Mail mit einem Link zum Zurücksetzen wurde an{' '}
                    <span className="font-medium">{resetEmail}</span> gesendet.
                    Bitte prüfen Sie auch Ihren Spam-Ordner.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => { setResetMode(false); setResetSuccess(false); }}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück zur Anmeldung
                </Button>
              </div>
            ) : (
              /* Reset-Formular */
              <form onSubmit={handleReset} className="space-y-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.
                  </p>
                </div>

                <Input
                  label="E-Mail-Adresse"
                  type="email"
                  placeholder="arzt@praxis.de"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />

                {resetError && (
                  <div role="alert" aria-live="assertive" className="rounded-lg border border-error/30 bg-error-light/20 px-3 py-2 text-sm text-error-dark">
                    {resetError}
                  </div>
                )}

                <Button type="submit" className="w-full" loading={resetLoading} disabled={resetLoading || !configured}>
                  Link zum Zurücksetzen senden
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setResetMode(false); setResetError(null); }}
                    className="inline-flex items-center text-sm text-brand-600 hover:text-brand-700 hover:underline transition-colors"
                  >
                    <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                    Zurück zur Anmeldung
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-neutral-500 dark:text-neutral-500">
          Nur für autorisiertes Praxispersonal. Geschützt nach DSGVO.
        </p>
      </div>
    </div>
  );
}
